import React, { useCallback, useMemo, useState } from 'react';
import { Card, List, Button, Typography, Empty, Input } from 'antd';
import {
  PlayCircleFilled,
  PauseCircleFilled,
  CustomerServiceOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import musicData from '../data/musicTracks.json';
import { MusicTrack } from '../types';

const { Text } = Typography;

const tracks: MusicTrack[] = musicData as MusicTrack[];

const MusicPlayer: React.FC = () => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [search, setSearch] = useState('');

  const currentTrack = useMemo(
    () => tracks.find((t) => t.id === currentId) ?? null,
    [currentId]
  );

  const filteredTracks = useMemo(() => {
    if (!search.trim()) return tracks;
    const lower = search.trim().toLowerCase();
    return tracks.filter((t) => t.title.toLowerCase().includes(lower));
  }, [search]);

  const playTrack = useCallback((track: MusicTrack) => {
    setCurrentId(track.id);
    setIsPlaying(true);
  }, []);

  const handleItemClick = useCallback(
    (track: MusicTrack) => {
      if (track.id === currentId) {
        setIsPlaying((prev) => !prev);
      } else {
        playTrack(track);
      }
    },
    [currentId, playTrack]
  );

  const handleStep = useCallback(
    (direction: 1 | -1) => {
      if (!tracks.length) return;
      const currentPos = tracks.findIndex((t) => t.id === currentId);
      const nextPos =
        currentPos === -1
          ? 0
          : (currentPos + direction + tracks.length) % tracks.length;
      playTrack(tracks[nextPos]);
    },
    [currentId, playTrack]
  );

  const embedSrc = currentTrack
    ? `${currentTrack.url}${isPlaying ? '?autoplay=1' : '?autoplay=0'}`
    : undefined;

  return (
    <Card className="music-card" title="Âm nhạc thư giãn">
      <Input
        placeholder="Tìm bản nhạc..."
        prefix={<SearchOutlined />}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="music-search"
      />

      <div className="music-player-embed">
        {currentTrack ? (
          <>
            <iframe
              key={currentTrack.id}
              title={currentTrack.title}
              width="100%"
              height="200"
              src={embedSrc}
              allow="autoplay; encrypted-media"
              allowFullScreen
              style={{ border: 0, borderRadius: 12 }}
            />
            <div className="music-now-playing">
              <Text strong>{currentTrack.title}</Text>
              <div className="music-transport">
                <Button
                  shape="circle"
                  icon={<StepBackwardOutlined />}
                  onClick={() => handleStep(-1)}
                />
                <Button
                  shape="circle"
                  type="primary"
                  icon={
                    isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />
                  }
                  onClick={() => setIsPlaying((prev) => !prev)}
                />
                <Button
                  shape="circle"
                  icon={<StepForwardOutlined />}
                  onClick={() => handleStep(1)}
                />
              </div>
            </div>
          </>
        ) : (
          <Empty
            image={<CustomerServiceOutlined className="music-empty-icon" />}
            description="Chọn một bản nhạc bên dưới để bắt đầu"
          />
        )}
      </div>

      <List
        className="music-list"
        dataSource={filteredTracks}
        locale={{ emptyText: 'Không tìm thấy bản nhạc' }}
        renderItem={(track) => {
          const active = track.id === currentId;
          return (
            <List.Item
              className={active ? 'music-list-item active' : 'music-list-item'}
              onClick={() => handleItemClick(track)}
            >
              <List.Item.Meta
                avatar={
                  active && isPlaying ? (
                    <PauseCircleFilled className="music-list-icon" />
                  ) : (
                    <PlayCircleFilled className="music-list-icon" />
                  )
                }
                title={track.title}
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
};

export default MusicPlayer;
